/**
 * Build-time / run-time logger configuration.
 *
 * • Works in Node, Storybook and browser tests without leaking secrets.
 * • Keeps the env-access helper in one place (DRY, SoC).
 * • Type-safe and guarded against circular-import timing.
 */
/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */
/** True when executed inside Storybook (regardless of Jest) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const win = globalThis.window;
const isStorybook = typeof win !== 'undefined' && typeof win?.__STORYBOOK_ADDONS__ !== 'undefined';
/** Safe env reader that also works in browser-only tests */
const env = (key, fallback = '') => typeof process !== 'undefined' && process.env ? (process.env[key] ?? fallback) : fallback;
/* -------------------------------------------------------------------------- */
/* Factory                                                                    */
/* -------------------------------------------------------------------------- */
export const buildLoggerConfig = () => ({
    level: (isStorybook ? 'warn' : env('LOG_LEVEL', 'info')),
    elasticsearch: {
        enabled: !isStorybook && Boolean(env('ELASTICSEARCH_URL')),
        node: env('ELASTICSEARCH_URL', 'http://localhost:9200'),
        indexPrefix: env('INDEX_PREFIX', 'app-logs'),
    },
    sentry: {
        dsn: env('SENTRY_DSN'),
        environment: isStorybook ? 'storybook' : env('NODE_ENV', 'development'),
    },
    serviceName: env('SERVICE_NAME', isStorybook ? 'storybook-service' : 'my-service'),
});
/**
 * Shared logger configuration instance used across the logger package.
 * Building it once avoids repeating environment lookups.
 */
export const loggerConfig = buildLoggerConfig();
/* -------------------------------------------------------------------------- */
/* Diagnose early (Node-only)                                                 */
/* -------------------------------------------------------------------------- */
if (!isStorybook) {
    // Wrap in try/catch to dodge circular-import races when requiring logger.ts
    void import('../logger.js')
        .then(({ logger }) => {
        if (logger?.debug) {
            const cfg = buildLoggerConfig();
            logger.debug('Logger configuration', cfg);
            if (!cfg.elasticsearch.enabled) {
                logger.warn('Elasticsearch logging is disabled – logs stay on console.');
            }
            if (!cfg.sentry.dsn) {
                logger.warn('Sentry DSN missing – errors will not be reported.');
            }
        }
    })
        .catch(() => {
        /* logger not yet constructed (tests or circular import) – ignore */
    });
}
//# sourceMappingURL=loggerConfig.js.map